import { ComponentPropsWithoutRef, ReactElement, useMemo, useState } from 'react';
import ModalWrapper from '../../components/modals/ModalWrapper';
import ModalHeader from '../../components/modals/ModalHeader';
import { useTranslation } from 'react-i18next';
import { IonItem, IonLabel, IonList } from '@ionic/react';
import classnames from 'classnames';
import ProfileIcon from '../../assets/icons/ProfileIcon';
import { IonAlertCustomEvent, OverlayEventDetail } from '@ionic/core';
import LogoutIcon from '../../assets/icons/LogoutIcon';
import HelpAndFaqsIcon from '../../assets/icons/HelpAndFaqsIcon';
import PaymentMethodIcon from '../../assets/icons/PaymentMethodIcon';
import { Modals } from '../../exports/Enums';
import { IModal } from '../../router/ModalRouter';
import Alert from '../../components/modals/Alert';
import useAuth from '../../../../hooks/global/useAuth';
import LanguageIcon from '../../assets/icons/LanguageIcon';

interface AccountItem {
  title: string;
  description?: string;
  type: AccountItemEnum;
  icon: ReactElement;
  props?: ComponentPropsWithoutRef<typeof IonItem>;
}

type AccountItemHandler = Record<AccountItemEnum, () => void>;

export enum AccountItemEnum {
  ManageAccount = 'manage-account',
  PaymentMethods = 'payment-methods',
  Language = 'language',
  HelpAndFAQs = 'help-and-faqs',
  Logout = 'logout',
}

const ProfileModal = ({ openModal, closeModal }: Partial<IModal>) => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Features:Profile:Components:ProfileModal:ProfileModal',
  });
  const { logout, isLoggedIn } = useAuth();
  const [isLogoutAlertOpen, setIsLogoutAlertOpen] = useState(false);

  const toggleLogoutAlert = (event?: IonAlertCustomEvent<OverlayEventDetail>): void => {
    setIsLogoutAlertOpen((prev) => !prev);

    if (event?.detail?.role === 'confirm') {
      closeModal();
    }
  };

  const items: AccountItem[] = [
    isLoggedIn && {
      title: t(`items.${AccountItemEnum.ManageAccount}.title`),
      description: t(`items.${AccountItemEnum.ManageAccount}.description`),
      icon: <ProfileIcon className="stroke-black size-6" />,
      type: AccountItemEnum.ManageAccount,
    },
    isLoggedIn && {
      title: t(`items.${AccountItemEnum.PaymentMethods}.title`),
      description: t(`items.${AccountItemEnum.PaymentMethods}.description`),
      icon: <PaymentMethodIcon className="stroke-black size-6" />,
      type: AccountItemEnum.PaymentMethods,
    },
    {
      title: t(`items.${AccountItemEnum.Language}.title`),
      icon: <LanguageIcon className="size-6" />,
      type: AccountItemEnum.Language,
    },
    {
      title: t(`items.${AccountItemEnum.HelpAndFAQs}.title`),
      icon: <HelpAndFaqsIcon className="stroke-black size-6" />,
      type: AccountItemEnum.HelpAndFAQs,
    },
    isLoggedIn && {
      title: t(`items.${AccountItemEnum.Logout}.title`),
      icon: <LogoutIcon className="stroke-black size-6" />,
      type: AccountItemEnum.Logout,
    },
  ].filter(Boolean);

  const ACCOUNT_ITEM_HANDLERS: AccountItemHandler = {
    [AccountItemEnum.ManageAccount]: () => openModal({ id: Modals.ManageAccount }),
    [AccountItemEnum.PaymentMethods]: () => openModal({ id: Modals.PaymentMethods }),
    [AccountItemEnum.Language]: () => openModal({ id: Modals.LanguageSwitcher }),
    [AccountItemEnum.HelpAndFAQs]: () => openModal({ id: Modals.HelpAndFAQs }),
    [AccountItemEnum.Logout]: toggleLogoutAlert,
  };

  const logoutAlert = useMemo(
    () => (
      <Alert
        title={'Are you sure you want to logout?'}
        isOpen={isLogoutAlertOpen}
        onDidDismiss={toggleLogoutAlert}
        buttons={[
          {
            text: 'Logout',
            role: 'confirm',
            handler: logout,
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />
    ),
    [isLogoutAlertOpen],
  );
  return (
    <ModalWrapper>
      <ModalHeader title={t('title')} onClose={closeModal} />
      <IonList>
        {logoutAlert}
        {items.map(({ props: { className, ...rest } = {}, title, description, icon, type }) => (
          <IonItem
            button
            className={classnames('[&::part(native)]:px-md space-x-md', className)}
            style={{
              '--inner-padding-end': 0,
            }}
            key={type}
            {...rest}
            onClick={ACCOUNT_ITEM_HANDLERS[type]}
          >
            {icon}
            <IonLabel className="pr-md">
              <h3 className="!font-semibold text-sm">{title}</h3>
              {description && <p className="!text-xs !text-gray-700 font-medium">{description}</p>}
            </IonLabel>
          </IonItem>
        ))}
      </IonList>
    </ModalWrapper>
  );
};

export default ProfileModal;
